import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { categories, allArticles } from '@/mocks/data';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  if (!article) {
    return (
      <div className="min-h-screen bg-background-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-200 font-heading mb-3">文章未找到</h1>
          <Link to="/" className="text-primary-400 hover:text-primary-300 transition-colors text-sm">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const cat = categories.find((c) => c.slug === article.category);
  const relatedArticles = allArticles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: 'NTR大师',
    },
    publisher: {
      '@type': 'Organization',
      name: 'NTR大师',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://ntrmaster.com' },
      { '@type': 'ListItem', position: 2, name: cat?.name || '', item: `https://ntrmaster.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} | NTR大师`}
        description={article.excerpt}
        keywords={`NTR大师,${article.title},NTR内容,NTR创作,${cat?.name || ''}`}
        canonicalUrl={`https://ntrmaster.com/article/${article.slug}`}
        ogImage={article.image}
        ogType="article"
        schemaData={[articleSchema, breadcrumbSchema]}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <div className="pt-24 pb-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: cat?.name || '', path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-16">
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 bg-primary-500/10 text-primary-400 text-xs rounded-full hover:bg-primary-500/20 transition-colors cursor-pointer"
              >
                {cat?.name}
              </Link>
              <span className="text-xs text-foreground-600">{article.date}</span>
              <span className="text-xs text-foreground-600">·</span>
              <span className="text-xs text-foreground-600">{article.readTime}</span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-100 font-heading leading-tight">
              {article.title}
            </h1>
          </header>

          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - NTR大师`}
              className="w-full h-auto object-cover max-h-[450px] object-top"
            />
          </div>

          <div
            className="article-content text-sm md:text-base"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-800">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-foreground-500">标签：</span>
              <span className="px-3 py-1 bg-background-900 text-foreground-500 text-xs rounded-full border border-background-800">
                {cat?.name}
              </span>
              <span className="px-3 py-1 bg-background-900 text-foreground-500 text-xs rounded-full border border-background-800">
                NTR创作
              </span>
              <span className="px-3 py-1 bg-background-900 text-foreground-500 text-xs rounded-full border border-background-800">
                NTR大师
              </span>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <aside className="mt-12 pt-8 border-t border-background-800">
              <h2 className="text-lg font-semibold text-foreground-200 font-heading mb-6">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group p-4 rounded-lg bg-background-900 border border-background-800 hover:border-primary-400/20 transition-all cursor-pointer"
                  >
                    <div className="w-full h-32 rounded-md overflow-hidden mb-3">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        title={`${ra.title} - NTR大师`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-300 group-hover:text-primary-300 transition-colors line-clamp-2 font-heading">
                      {ra.title}
                    </h3>
                    <span className="text-[11px] text-foreground-600 mt-2 block">{ra.date}</span>
                  </Link>
                ))}
              </div>
            </aside>
          )}

          <section className="mt-12 pt-8 border-t border-background-800">
            <h2 className="text-lg font-semibold text-foreground-200 font-heading mb-6">
              订阅NTR大师动态
            </h2>
            <form
              data-readdy-form
              id="article-newsletter"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                const hpVal = (formData.get('website_alt') as string || '').trim();
                if (hpVal) {
                  setFormStatus('success');
                  return;
                }
                formData.delete('website_alt');
                try {
                  const res = await fetch('https://readdy.ai/api/form/d9spbt5lbrt1qcgaj7gg', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                  });
                  const text = await res.text();
                  let parsed: Record<string, unknown> = {};
                  try { parsed = JSON.parse(text); } catch { /* ignore */ }
                  if (
                    res.ok &&
                    parsed &&
                    typeof parsed === 'object' &&
                    (parsed as Record<string, unknown>).code === 'OK'
                  ) {
                    setFormStatus('success');
                    setFormError('');
                  } else {
                    const msg =
                      (parsed &&
                        ((parsed as Record<string, unknown>).meta as Record<string, unknown>)?.message) ||
                      (parsed as Record<string, unknown>)?.message ||
                      '';
                    const msgStr = String(msg);
                    if (text.includes('spam') || msgStr.includes('spam') || msgStr.includes('form data is spam')) {
                      setFormError(msgStr || '提交失败，请稍后再试');
                    } else {
                      setFormError(msgStr || '提交失败，请稍后再试');
                    }
                    setFormStatus('error');
                  }
                } catch {
                  setFormError('网络错误，请检查网络连接后重试');
                  setFormStatus('error');
                }
              }}
              className="flex flex-col sm:flex-row gap-3 max-w-md"
            >
              <input
                type="email"
                name="email"
                placeholder="输入您的邮箱地址"
                required
                className="flex-1 px-4 py-2.5 bg-background-900 border border-background-700 rounded-lg text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400/50 transition-colors"
              />
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="contact-honeypot"
              />
              <button
                type="submit"
                className="px-5 py-2.5 bg-primary-500 text-background-950 text-sm font-medium rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                订阅
              </button>
            </form>
            {formStatus === 'success' && (
              <p className="mt-3 text-sm text-green-400">订阅成功！感谢您的关注。</p>
            )}
            {formStatus === 'error' && formError && (
              <p className="mt-3 text-sm text-red-400">{formError}</p>
            )}
          </section>
        </article>

        <Footer />
      </div>
    </>
  );
}